import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { Params, useLocation, useParams } from 'react-router-dom'
import './Player.scss'
import { getVideoInfo } from '@utils/player.util'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import { useTypedSelector } from '../../store'
import { setIsPlaying, setAudioList, setSubtitle, setSubtitleList, setAudio } from '@actions/playerActions'
import classNames from 'classnames'
import { setError } from '@actions/errorCodeActions'
import { ErrorActions, ErrorCodes } from '@constants/index'
import ErrorCodeModal from '@components/ErrorCodeModal/ErrorCodeModal'
import appConfig from '../../app_config.json'
import { PlayerControls } from '@components/PlayerControls/PlayerControls'
import { useDispatch } from 'react-redux'
import { playerId, PlayerName, disabledSubtitleValue, disabledAudioValue } from '@constants/player.const'
import { useImgUrlTransform } from '@hooks/useImgUrlTransform'
import { ImageExtension, ImageSize } from '@enums/images.enum'
import { VelvetSDK, VideoPlayer, VideoAnalytics } from 'sdk'
import { VideoEntitlementType } from 'sdk/services/cms/types/cmsService'
import { MediaItem } from 'sdk/services/videoPlayer/types/videoPlayer'
import { PlayerError } from '@interfaces/index'
import PlayerRecommendation from '@components/PlayerRecommendation/PlayerRecommendation'
import { getActiveProfile, onEnterKeyPressAndClick, timeStringToSeconds } from '@utils/helpers'
import { categoryType } from '@constants/category.const'

interface PlayerProps {
  showMenu: (status: boolean) => void
}

export const Player = ({ showMenu }: PlayerProps) => {
  const [videoConfig, setVideoConfig] = useState<VideoEntitlementType | null>(null)
  const [player, setPlayer] = useState<VideoPlayer | null>(null)
  const [analytics, setAnalytics] = useState<VideoAnalytics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasPlaybackEnded, setHasPlaybackEnded] = useState<boolean>()
  const [currentTime, setCurrentTime] = useState(0)
  const playerDiv = useRef<HTMLDivElement>(null)
  const progressRef = useRef<HTMLProgressElement>(null)
  const { slug } = useParams<Params>()
  const location = useLocation()
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)
  const savedSubtitle = useTypedSelector((state) => state.player.subtitle)
  const savedAudio = useTypedSelector((state) => state.player.audio)
  const subtitlesList = useTypedSelector((state) => state.player.subtitleList)
  const audioList = useTypedSelector((state) => state.player.audioList)
  const findDefaultAudio = audioList.length > 0 && audioList?.find((audio) => audio?.default === true)
  const findDefaultSubtitle = subtitlesList.length > 0 && subtitlesList?.find((subtitle) => subtitle?.default === true)
  const config = VelvetSDK.Configuration.configuration
  const userCountry = useTypedSelector((state) => state.userCountry.data.countryCode)
  const bitmovinPlayer = appConfig.services.video_player.vendor_name === PlayerName.BITMOVIN
  const hlsPlayer = appConfig.services.video_player.vendor_name === PlayerName.HLS
  const muxPlayer = appConfig.services.video_player.vendor_name === PlayerName.MUX
  const [adIsPlaying, setAdIsPlaying] = useState(false)
  const showErrorModal = useTypedSelector((state) => state.errorCode.showErrorModal)
  const errorAction = useTypedSelector((state) => state.errorCode?.fetchAction)
  const [isInitialized, setIsInitialized] = useState(false)
  const [showControls, setShowControls] = useState(false)
  const [duration, setDuration] = useState<number>()
  const bitmovinDefaultAudio = bitmovinPlayer && player && isInitialized && !adIsPlaying && player?.getAudio()
  const [showRecommendationScreen, setShowRecommendationScreen] = useState(false)
  const [recommendedData, setRecommendedData] = useState<MediaItem | null>(null)
  const [openCredits, setOpenCredits] = useState(false)
  const [isRecommendedFetched, setIsRecommendedFetched] = useState(false)
  const [isCreditScreenOpened, setIsCreditScreenOpened] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const activeProfile = getActiveProfile()
  const endCreditsAfterSec = VelvetSDK.Configuration.configuration.features.recommendations.endCreditsAfterSec

  const creditsStartTime = videoConfig?.contextData.creditsStartTime
    ? timeStringToSeconds(videoConfig?.contextData.creditsStartTime)
    : duration && duration * config.features.resumeWatching.maxWatchedPercentage

  const apiCallBeforeEndCreditsSec =
    VelvetSDK.Configuration.configuration.features.recommendations.apiCallBeforeEndCreditsSec

  const showRecommendationScreenCondition =
    creditsStartTime &&
    Math.floor(currentTime) >= creditsStartTime + endCreditsAfterSec &&
    !isCreditScreenOpened &&
    recommendedData

  const startFetching =
    videoConfig && creditsStartTime !== undefined ? creditsStartTime - apiCallBeforeEndCreditsSec : null
  const fetchRecommendation =
    startFetching !== null && Math.floor(currentTime) >= startFetching && !isRecommendedFetched

  const shouldShowRecommendationScreen =
    (showRecommendationScreen && !openCredits) || (recommendedData && duration === currentTime)

  const shouldShowControls = showControls && duration && currentTime > 0 && duration !== currentTime

  const videoThumbnailPhoto = useImgUrlTransform(
    videoConfig?.contextData?.Thumbnail ?? '',
    ImageSize.VIDEO_THUMBNAIL,
    ImageExtension.WEBP,
  )

  const audioFromStore = useMemo(
    () => audioList?.find((audio) => audio?.lang.toString() === savedAudio?.id),
    [audioList, savedAudio],
  )

  const subtitleFromStore = useMemo(
    () => subtitlesList?.find((subtitle) => subtitle?.lang.toString() === savedSubtitle?.lang),
    [subtitlesList, savedSubtitle],
  )

  const playerConfig = useMemo(() => {
    if (bitmovinPlayer) {
      return {
        key: appConfig.services.video_player.API_KEY,
        playerContainerId: playerId,
        tweaks: {
          file_protocol: true,
          app_id: appConfig.services.video_player.otherValues.app_id,
          BACKWARD_BUFFER_PURGE_INTERVAL: 10,
          DWORD_BASE_MEDIA_DECODE_TIMESTAMPS: true,
        },
      }
    } else if (hlsPlayer) {
      return {
        playerContainerId: playerId,
      }
    } else if (muxPlayer) {
      return {
        playerContainerId: playerId,
      }
    }
  }, [bitmovinPlayer, hlsPlayer, muxPlayer])

  const play = useCallback(async () => {
    if (player) {
      dispatch(setIsPlaying(true))
      try {
        await player.play()
      } catch (err) {
        console.error('Player play() error:', err)
      }
    }
  }, [dispatch, player])

  const pause = useCallback(async () => {
    if (player) {
      dispatch(setIsPlaying(false))
      await player.pause()
    }
  }, [dispatch, player])

  const playFromBeginning = useCallback(async () => {
    if (player) {
      player.seek(0)
      await play()
      hasPlaybackEnded && setHasPlaybackEnded(false)
    }
  }, [player, hasPlaybackEnded, play])

  const playFromPosition = useCallback(
    async (position: number) => {
      if (player) {
        player.seek(position)
        setCurrentTime(position)
        await play()

        if (position < (duration || 0) - 1) {
          // Only reset if not near the end
          setHasPlaybackEnded(false)
        }
      }
    },
    [player, setCurrentTime, play, duration],
  )

  const setAudioDispatch = useCallback(
    (audioLanguage: string, audioId: string) => dispatch(setAudio({ lang: audioLanguage, id: audioId?.toString() })),
    [dispatch],
  )

  const setSubtitleDispatch = useCallback(
    (subtitleLanguage: string, subtitleId: string) => dispatch(setSubtitle({ lang: subtitleLanguage, id: subtitleId })),
    [dispatch],
  )

  const handleOnFocus = () => {
    setIsFocused(true)
  }

  const handleOnBlur = () => {
    setIsFocused(false)
  }

  const handleClick = (e: React.KeyboardEvent | React.MouseEvent) => {
    if (showRecommendationScreen) {
      onEnterKeyPressAndClick(e, () => {
        //Video is still playing (not ended) and recommendation screen is shown
        //on click on the video it should enlarge the video player again
        if (!hasPlaybackEnded) {
          setOpenCredits(false)
          setIsCreditScreenOpened(true)
        } else {
          //Video has ended and recommendation screen is shown
          setOpenCredits(true)
          setShowRecommendationScreen(false)
          setIsCreditScreenOpened(false)
        }
      })
    }
  }

  useEffect(() => {
    showMenu(false)
  }, [showMenu])

  useEffect(() => {
    const fetchVideoInfo = async () => {
      setIsLoading(true)

      if (slug && activeProfile?.preferredLanguage) {
        try {
          const config = await getVideoInfo(slug, activeProfile?.preferredLanguage)
          // current
          // TODO handle this in the future possibly on the sdk to validate streamsUrl
          if (!config?.streamUrls?.length) {
            throw new Error('Stream is empty')
          }
          if (!config.error) {
            setVideoConfig(config)
            setIsLoading(false)

            if (errorAction === ErrorActions.ENTITLEMENT) {
              dispatch(setError({ errorCode: '', showErrorModal: false }))
            }
          }
        } catch (error) {
          setIsLoading(false)
          setVideoConfig(null)

          dispatch(
            setError({
              showErrorModal: true,
              errorCode: ErrorCodes.FETCH_ENTITLEMENT,
              HTTPcode: 400,
              videoSlug: slug,
              fetchAction: ErrorActions.ENTITLEMENT,
              pathLocation: location.pathname.split('/')[1],
            }),
          )
        }
      }
    }

    fetchVideoInfo()

    return () => {
      setVideoConfig(null)
      dispatch(setIsPlaying(false))
    }
  }, [bitmovinPlayer, dispatch, errorAction, hlsPlayer, location.pathname, slug, activeProfile?.preferredLanguage])

  useEffect(() => {
    if (playerConfig && videoConfig && appConfig.services && activeProfile?.identifier) {
      VelvetSDK.configurePlayer(
        playerConfig,
        {
          ...videoConfig,
          contextData: {
            ...videoConfig.contextData,
            Thumbnail: videoThumbnailPhoto,
          },
        },
        activeProfile?.identifier,
      )

      VelvetSDK.Player.service
        .then((playerInstance) => {
          setPlayer(playerInstance)
        })
        .catch((error) => {
          console.error('Error initializing the player:', error)
        })
    }
  }, [videoThumbnailPhoto, videoConfig, bitmovinPlayer, playerConfig, activeProfile?.identifier])

  useEffect(() => {
    if (appConfig.services.video_analytics) {
      VelvetSDK.Analytics.service
        .then((videoAnalytics) => {
          setAnalytics(videoAnalytics)
        })
        .catch((error) => {
          console.error('Error initializing the analytics:', error)
        })
    }
  }, [])

  useEffect(() => {
    player &&
      player.initialize().then(() => {
        setIsInitialized(true)

        // Get duration immediately after initialization (source is already loaded)
        const videoDuration = player.getDuration()
        setDuration(videoDuration)

        if (videoDuration !== undefined && videoDuration <= 0) {
          throw new Error('Invalid video duration')
        }

        const playerInstance = player.getPlayerInstance()
        const videoAnalyticsConfig = {
          playerInstance: playerInstance,
          videoId: videoConfig?.id,
        }
        analytics && analytics.setup(videoAnalyticsConfig)
        play()
      })

    return () => {
      player && player.destroy()
      analytics?.destroy && analytics.destroy()
    }
  }, [player, play, analytics, videoConfig?.id])

  useEffect(() => {
    // For Bitmovin: duration is already set after initialization, show controls when playback starts
    if (player && isInitialized && bitmovinPlayer && duration && currentTime > 0) {
      setShowControls(true)
    }

    // For HLS: wait for media to be attached, then get duration and show controls
    if (player && isInitialized && hlsPlayer) {
      player.on('hlsMediaAttached', () => {
        setDuration(player.getDuration())
        setShowControls(true)
      })
    }

    if (player && isInitialized && muxPlayer) {
      const handleReady = () => {
        setDuration(player.getDuration())
        setCurrentTime(0.01)
        setShowControls(true)
      }

      if (player.getDuration() > 0) {
        handleReady()
      } else {
        player.on('loadedmetadata', handleReady)
      }
    }
  }, [player, isInitialized, bitmovinPlayer, adIsPlaying, duration, hlsPlayer, muxPlayer])

  useEffect(() => {
    if (player && isInitialized) {
      if (player.getSubtitleList && player.getAudioList) {
        Promise.all([player.getSubtitleList(), player.getAudioList()]).then(([subtitles, audioTracks]) => {
          dispatch(setSubtitleList(subtitles))
          dispatch(setAudioList(audioTracks))
        })
      }

      const trackTime = () => {
        if (player && isInitialized) {
          if (bitmovinPlayer) {
            setCurrentTime(player.getCurrentTime())
          } else if (hlsPlayer) {
            const videoElement = player.getVideoElement()
            if (videoElement) {
              setCurrentTime(videoElement.currentTime)
            }
          } else if (muxPlayer) {
            setCurrentTime(player.getCurrentTime())
          }
        }
      }

      if (bitmovinPlayer) {
        player.on('timechanged', trackTime)
      } else if (hlsPlayer) {
        const hlsVideo = playerDiv.current?.children[0]
        hlsVideo?.addEventListener('timeupdate', trackTime)
      } else if (muxPlayer) {
        // For Mux, wait for the mux-player element to be available
        const attachMuxTimeUpdate = () => {
          const container = document.getElementById(playerId)
          const muxElement = container?.querySelector('mux-player')
          if (muxElement) {
            muxElement.addEventListener('timeupdate', trackTime)
          } else {
            setTimeout(attachMuxTimeUpdate, 100)
          }
        }
        attachMuxTimeUpdate()
      }
    }
  }, [player, isInitialized, bitmovinPlayer, hlsPlayer, muxPlayer, dispatch])

  useEffect(() => {
    if (player && isInitialized) {
      if (bitmovinPlayer) {
        player.on('playbackfinished', () => {
          setAdIsPlaying(false)
          setHasPlaybackEnded(true)
          setCurrentTime(player.getDuration())
        })

        player.on('adstarted', () => {
          setAdIsPlaying(true)
          setShowControls(false)
        })

        player.on('aderror', () => {
          setAdIsPlaying(false)
          setShowControls(true)
        })

        player.on('adfinished', () => {
          setAdIsPlaying(false)
          setCurrentTime(player.getCurrentTime())
          setShowControls(true)
        })

        player.on('ready', () => {
          setIsLoading(false)
        })

        player.on('error', (error: PlayerError) => {
          console.error('Error on player:', error)
          dispatch(
            setError({
              showErrorModal: true,
              errorCode: ErrorCodes.FETCH_ENTITLEMENT,
              HTTPcode: error.code,
              videoSlug: slug,
              fetchAction: ErrorActions.ENTITLEMENT,
              pathLocation: location.pathname.split('/')[1],
            }),
          )
        })
      }

      if (hlsPlayer) {
        const duration = player && player.getDuration()
        setDuration(duration)
        if (currentTime && currentTime > 0 && Math.floor(currentTime) === Math.floor(duration)) {
          setAdIsPlaying(false)
          setHasPlaybackEnded(true)
        }
      }

      if (muxPlayer) {
        const videoElement = player.getVideoElement()

        const onEnded = () => {
          setAdIsPlaying(false)
          setHasPlaybackEnded(true)
          setCurrentTime(player.getDuration())
        }

        videoElement?.addEventListener('ended', onEnded)

        player.on('error', (error: Event) => {
          console.error('Mux player error:', error)
          dispatch(
            setError({
              showErrorModal: true,
              errorCode: ErrorCodes.FETCH_ENTITLEMENT,
              HTTPcode: 500,
              videoSlug: slug,
              fetchAction: ErrorActions.ENTITLEMENT,
              pathLocation: location.pathname.split('/')[1],
            }),
          )
        })
      }
    }
  }, [bitmovinPlayer, currentTime, dispatch, hlsPlayer, muxPlayer, isInitialized, location.pathname, player, slug])

  useEffect(() => {
    if (videoConfig?.adsConfig.length === 0) {
      setAdIsPlaying(false)
      setShowControls(true)
    }
  }, [videoConfig?.adsConfig.length])

  useEffect(() => {
    if (player && bitmovinPlayer) {
      const bitmovinVideo = playerDiv.current?.children[0]

      bitmovinVideo?.setAttribute('crossorigin', '')

      const currentSubtitle = subtitlesList && subtitlesList.find((subtitle) => subtitle.lang === savedSubtitle.lang)

      const trackElement = document.querySelector('track')
      trackElement && trackElement.remove()

      if (currentSubtitle) {
        const trackElement = document.createElement('track')
        trackElement.default = true
        trackElement.kind = 'captions'
        trackElement.srclang = currentSubtitle.lang
        trackElement.src = currentSubtitle.url

        bitmovinVideo?.appendChild(trackElement)
      }
    }
  }, [bitmovinPlayer, player, savedSubtitle, subtitlesList])

  useEffect(() => {
    if (player && isInitialized) {
      const handleBitmovinPlayer = () => {
        if (!savedAudio?.id) {
          bitmovinDefaultAudio && setAudioDispatch(bitmovinDefaultAudio, bitmovinDefaultAudio)
        } else {
          audioFromStore && player.setAudio(audioFromStore?.id)
        }
      }

      const handleHlsPlayer = () => {
        if (!savedAudio.id) {
          if (findDefaultAudio && savedAudio?.id !== findDefaultAudio?.id) {
            setAudioDispatch(findDefaultAudio.lang, findDefaultAudio.id)
          }
        } else if (savedAudio.id && savedAudio.id !== disabledAudioValue) {
          player.setAudio(Number(savedAudio.id))
        } else {
          const defaultAudio = player?.getAudio().toString()
          setAudioDispatch(savedAudio.lang, defaultAudio)
        }
      }

      switch (true) {
        case bitmovinPlayer:
          handleBitmovinPlayer()
          break
        case hlsPlayer:
          handleHlsPlayer()
          break
        case muxPlayer:
          if (!savedAudio?.id) {
            findDefaultAudio && setAudioDispatch(findDefaultAudio.lang, findDefaultAudio.id)
          } else if (savedAudio.id && savedAudio.id !== disabledAudioValue) {
            player.setAudio(savedAudio.id)
          }
          break
        default:
          break
      }
    }
  }, [
    bitmovinPlayer,
    dispatch,
    audioFromStore?.id,
    findDefaultAudio,
    hlsPlayer,
    muxPlayer,
    isInitialized,
    player,
    showControls,
    savedAudio,
    setAudioDispatch,
    adIsPlaying,
    bitmovinDefaultAudio,
    audioFromStore,
  ])

  useEffect(() => {
    if (player && isInitialized && showControls) {
      const handleNoSavedSubtitle = () => {
        if (hlsPlayer || muxPlayer) player.setSubtitle(-1)
        setSubtitleDispatch(disabledSubtitleValue, disabledSubtitleValue)
      }

      const handleSavedSubtitle = () => {
        if (subtitleFromStore) {
          if (hlsPlayer || muxPlayer) player.setSubtitle(Number(subtitleFromStore.id))
        } else if (savedSubtitle?.lang === disabledSubtitleValue || savedSubtitle?.lang === '') {
          if (hlsPlayer || muxPlayer) player.setSubtitle(-1)
        } else {
          if (findDefaultSubtitle) {
            if (hlsPlayer || muxPlayer) player.setSubtitle(-1)
            setSubtitleDispatch(disabledSubtitleValue, disabledSubtitleValue)
          }
        }
      }

      if (!savedSubtitle.lang) {
        handleNoSavedSubtitle()
      } else {
        handleSavedSubtitle()
      }
    }
  }, [
    dispatch,
    findDefaultSubtitle,
    subtitleFromStore,
    hlsPlayer,
    muxPlayer,
    isInitialized,
    player,
    savedSubtitle.lang,
    showControls,
    setSubtitleDispatch,
  ])

  useEffect(() => {
    if (!fetchRecommendation || !videoConfig || !activeProfile?.preferredLanguage) return
    // Fetch data for recommended movie
    if (videoConfig.parentType === categoryType.MOVIE) {
      setIsRecommendedFetched(true)
      VelvetSDK.CMS.service()
        .fetchRecommendationMovie(
          videoConfig.parentType,
          videoConfig.slug,
          activeProfile.preferredLanguage,
          userCountry
        )
        .then((data: MediaItem) => setRecommendedData(data))
    } else {
      // Fetch data for next episode
      setIsRecommendedFetched(true)
      if (videoConfig.seasonIndex !== undefined && videoConfig.episodeIndex !== undefined) {
        VelvetSDK.CMS.service()
          .fetchNextEpisode(
            videoConfig.slug,
            videoConfig.parentSlug,
            videoConfig.parentType,
            videoConfig.seasonIndex,
            videoConfig.episodeIndex,
            activeProfile.preferredLanguage,
            userCountry
          )
          .then((data) => {
            setRecommendedData(data)
          })
      }
    }
  }, [
    currentTime,
    fetchRecommendation,
    isRecommendedFetched,
    startFetching,
    activeProfile?.preferredLanguage,
    videoConfig?.episodeIndex,
    videoConfig?.parentSlug,
    videoConfig?.parentType,
    videoConfig?.seasonIndex,
    videoConfig?.slug,
    userCountry,
    videoConfig,
  ])

  useEffect(() => {
    //If the next episode doesn't exist there should be an error, and it
    //should fetch the next recommended series
    if (recommendedData?.error && videoConfig?.parentType) {
      VelvetSDK.CMS.service()
        .fetchRecommendationMovie(
          videoConfig?.parentType,
          videoConfig?.parentSlug,
          activeProfile?.preferredLanguage,
          userCountry
        )
        .then((data: MediaItem) => {
          setRecommendedData(data)
          setIsRecommendedFetched(true)
        })
    }
  }, [
    recommendedData?.error,
    activeProfile?.preferredLanguage,
    videoConfig?.parentSlug,
    videoConfig?.parentType,
    userCountry,
  ])

  useEffect(() => {
    if (videoConfig && showRecommendationScreenCondition) {
      setShowRecommendationScreen(true)
    } else if (showRecommendationScreen && !openCredits && isCreditScreenOpened) {
      setShowRecommendationScreen(false)
    }
  }, [isCreditScreenOpened, openCredits, showRecommendationScreen, showRecommendationScreenCondition, videoConfig])

  return (
    <div className={classNames('player-wrapper', adIsPlaying && 'disabled-pointer-events')}>
      {showErrorModal ? (
        <ErrorCodeModal />
      ) : (
        <>
          {isLoading && !showControls ? (
            <DotSpinner />
          ) : (
            <>
              <div
                id={playerId}
                ref={playerDiv}
                tabIndex={-1}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                onKeyDown={handleClick}
                className={classNames(
                  showRecommendationScreen && 'focusable',
                  isFocused && 'active',
                  openCredits && 'open',
                  showRecommendationScreen && hasPlaybackEnded && 'hide',
                )}
              />
              {!adIsPlaying &&
                !showRecommendationScreen &&
                (shouldShowControls ? (
                  <PlayerControls
                    play={play}
                    pause={pause}
                    hasPlaybackEnded={hasPlaybackEnded}
                    videoConfig={videoConfig}
                    playFromBeginning={playFromBeginning}
                    showModal={showModal}
                    setShowModal={setShowModal}
                    adIsPlaying={adIsPlaying}
                    currentTime={currentTime}
                    playFromPosition={playFromPosition}
                    progressRef={progressRef}
                    player={player}
                    isInitialized={isInitialized}
                    duration={duration}
                  />
                ) : (
                  <DotSpinner />
                ))}

              {shouldShowRecommendationScreen ? (
                <PlayerRecommendation
                  videoInfo={videoConfig}
                  recommendedData={recommendedData}
                  isRecommendedFetched={isRecommendedFetched}
                  userCountry={userCountry}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  )
}
